import { useMemo, useState, useCallback } from 'react'
import { Trash2, FileText, BarChart2, Building2 } from 'lucide-react'

import { DataTable, type Column } from '@/components/ui/data-table'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useUpdateList } from '@/entities/list'
import type { CrmListRow, CampaignShortItem } from '@intone/shared/types'

interface ListsTableProps {
  lists: CrmListRow[]
  campaigns: CampaignShortItem[]
  selectedCampaignId: number | null
  onViewLeads: (list: CrmListRow) => void
  onViewStats: (list: CrmListRow) => void
  onChangeCampaign: (list: CrmListRow) => void
  onDeleteList: (list: CrmListRow) => void
}

export function ListsTable({
  lists,
  campaigns: _campaigns,
  selectedCampaignId,
  onViewLeads,
  onViewStats,
  onChangeCampaign,
  onDeleteList,
}: ListsTableProps) {
  const updateList = useUpdateList()

  // Filter lists by selected campaign
  const filteredLists = useMemo(() => {
    if (!selectedCampaignId) return lists
    return lists.filter((l) => l.campaign_id === selectedCampaignId)
  }, [lists, selectedCampaignId])

  // Local edit states for inline fields
  const [nameEdits, setNameEdits] = useState<Record<number, string>>({})
  const [sourceEdits, setSourceEdits] = useState<Record<number, string>>({})

  const submitUpdate = useCallback(
    (id: number, payload: { name?: string; source?: string }) => {
      updateList.mutate({
        listId: id,
        data: payload,
      })
    },
    [updateList]
  )

  const columns: Column<CrmListRow>[] = useMemo(
    () => [
      {
        header: 'ID',
        accessorKey: 'id',
        sortField: 'id',
        width: { min: 80 },
        cell: (value: unknown) => String(value ?? ''),
      },
      {
        header: 'Name',
        accessorKey: 'name',
        sortField: 'name',
        width: { min: 200 },
        cell: (_value: unknown, row: CrmListRow) => (
          <Input
            value={nameEdits[row.id] ?? (row.name ?? `List ${row.id}`)}
            onChange={(e) => {
              const newValue = e.target.value
              setNameEdits((prev) => ({ ...prev, [row.id]: newValue }))
            }}
            onBlur={(e) => {
              const newValue = e.target.value.trim()
              const original = row.name ?? `List ${row.id}`
              if (newValue !== original) {
                submitUpdate(row.id, { name: newValue })
              }
            }}
            className="h-7 border-0 bg-transparent p-0 focus-visible:ring-0 focus-visible:ring-offset-0 hover:bg-gray-100 focus:bg-white w-full"
          />
        ),
      },
      {
        header: 'Source',
        accessorKey: 'source',
        sortField: 'source',
        width: { min: 150 },
        cell: (_value: unknown, row: CrmListRow) => (
          <Input
            value={sourceEdits[row.id] ?? (row.source || '')}
            onChange={(e) => {
              const newValue = e.target.value
              setSourceEdits((prev) => ({ ...prev, [row.id]: newValue }))
            }}
            onBlur={(e) => {
              const newValue = e.target.value.trim()
              const original = row.source || ''
              if (newValue !== original) {
                submitUpdate(row.id, { source: newValue })
              }
            }}
            placeholder="—"
            className="h-7 border-0 bg-transparent p-0 focus-visible:ring-0 focus-visible:ring-offset-0 hover:bg-gray-100 focus:bg-white w-full placeholder:text-gray-400"
          />
        ),
      },
      {
        header: 'Leads',
        accessorKey: 'size',
        sortField: 'size',
        width: { min: 100 },
        cell: (value: unknown) => ((value as number) ?? 0).toLocaleString(),
      },
      {
        header: 'Called',
        accessorKey: 'called_share',
        sortField: 'called_share',
        width: { min: 100 },
        cell: (value: unknown) => `${(((value as number) ?? 0) * 100).toFixed(1)}%`,
      },
      {
        header: 'Success',
        accessorKey: 'success_share',
        sortField: 'success_share',
        width: { min: 100 },
        cell: (value: unknown) => `${(((value as number) ?? 0) * 100).toFixed(1)}%`,
      },
      {
        header: 'Created',
        accessorKey: 'created_time',
        sortField: 'created_time',
        width: { min: 180 },
        cell: (value: unknown) => {
          const d = value ? new Date(value as string) : null
          return d
            ? `${d.toLocaleDateString('en-US', {
                month: '2-digit',
                day: '2-digit',
                year: 'numeric',
              })} ${d.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
              })}`
            : ''
        },
      },
    ],
    [nameEdits, sourceEdits, submitUpdate]
  )

  const actions = (row: CrmListRow) => (
    <div className="flex items-center justify-end gap-1">
      <Button
        variant="outline"
        size="sm"
        onClick={() => onViewLeads(row)}
        className="h-6 w-6 p-0"
        title="View Leads"
      >
        <FileText className="w-2.5 h-2.5" />
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={() => onViewStats(row)}
        className="h-6 w-6 p-0"
        title="View Stats"
      >
        <BarChart2 className="w-2.5 h-2.5" />
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={() => onChangeCampaign(row)}
        className="h-6 w-6 p-0"
        title="Change Campaign"
      >
        <Building2 className="w-2.5 h-2.5" />
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={() => onDeleteList(row)}
        className="h-6 w-6 p-0 text-red-500 hover:text-white hover:bg-red-500 border-red-200 hover:border-red-500"
        title="Delete List"
      >
        <Trash2 className="w-2.5 h-2.5" />
      </Button>
    </div>
  )

  return (
    <DataTable<CrmListRow>
      columns={columns}
      data={filteredLists}
      downloadFileName="lists.csv"
      actions={actions}
      defaultSort={{ field: 'created_time', direction: 'desc' }}
      pageSize={500}
    />
  )
}